import logger from '../../services/logging/logging-service';
import { getLabelText, truncateText } from '../../utils/string-utils';
import { skyFoundationSettings } from '../../services/configs/configs-service';
import { initDatalayerElement } from '../tracking/datalayer';
import { isNativeLazyLoadingSupported } from '../../utils/device-utils';
import { isParselyRecommendationEnabled } from '../../utils/aem-utils';

const config = require('config');

const lazyLoadingClass = config.lazyLoadingContents.lazyClass;
const placeholder = skyFoundationSettings.getStaticDomain() + config.placeholder.path;

const DEFAULT_ELEMENTS_TO_RENDER = 3;
const DEFAULT_ELEMENTS_TO_RENDER_RAIL = 4;
const SKYUNO_LABEL = 'sky uno';
const SKYCINEMA_LABEL = 'sky cinema';
const SKYATLANTIC_LABEL = 'sky atlantic';

const months = config.parselyRender.months || [
  'gen',
  'feb',
  'mar',
  'apr',
  'mag',
  'giu',
  'lug',
  'ago',
  'set',
  'ott',
  'nov',
  'dec',
];

/**
 * This function take a metadata and returns template with native lazy loading images OR fallback template
 * @param {*} metadata
 * @returns template for native lazy loading images OR fallback template
 */
const getImageTemplate = (metadata) => {
  if (isNativeLazyLoadingSupported()) {
    return `
    <img crossorigin="anonymous" class="c-card__img parselyImg ${lazyLoadingClass}"
    loading="lazy"
    src="${metadata.image_url}" sizes="(max-width: 1920px) 100vw"
    srcset="${metadata.image_url} 1x, ${metadata.image_url} 2x"`;
  } else {
    return `
    <img crossorigin="anonymous" class="c-card__img parselyImg ${lazyLoadingClass}"
    src="${placeholder}" sizes="(max-width: 1920px) 100vw"
    data-srcset="${metadata.image_url} 1x, ${metadata.image_url} 2x"
    data-src="${metadata.image_url}">`;
  }
};

/**
 * This is the template of the card
 * @param {object} metadata The object containing the infos of the single article (title, img, url)
 * @returns template of a single card
 */
const cardMarkup = (metadata, parselyTopNewsId = 'parselyTopNews') => {
  const hasImg = metadata.image_url.length > 1;
  return `
  <a class="${metadata.card_class} ${hasImg ? '' : ' c-card--no-image'}" href="${metadata.url}" data-intcmp="${
    metadata.intcmp
  }">
      <article class="c-card__wrapper">
          <div class="c-card__content">
              <h2 class="c-card__title">${
                document.querySelector(`#${parselyTopNewsId}`).dataset.position === 'rail'
                  ? truncateText(metadata.title, 50)
                  : metadata.title
              }</h2>
              ${metadata.cardLabelWrapper ? `<div class="c-card__label-wrapper c-label-wrapper">` : ''}
                ${
                  metadata.section
                    ? `<span class="c-label c-label--article-heading">${getLabelText(metadata.section)}</span>`
                    : ''
                }
                ${
                  metadata.channel
                    ? `<span class="c-label c-label--article-heading-light">${getLabelText(metadata.channel)}</span>`
                    : ''
                }
                ${
                  metadata.abstract
                    ? `
                            <p class="c-card__abstract">${metadata.abstract}</p>`
                    : ''
                }
                ${metadata.cardLabelWrapper ? `</div>` : ''}
                ${
                  metadata.hasDate
                    ? `<time class="c-card__date" datetime="${metadata.pub_date}">${metadata.fe_date}</time>`
                    : ``
                }
            </div>
        ${
          hasImg
            ? `<div class="c-card__img-wrapper">
          <figure class="o-aspect-ratio o-aspect-ratio--16-10">
            ${getImageTemplate(metadata)}
            <noscript>
              <img crossorigin="anonymous" class="c-card__img parselyImg"
                srcset="${metadata.image_url} 1x, ${metadata.image_url} 2x"
                src="${metadata.image_url}">
            </noscript>
          </figure>
        </div>`
            : ``
        }
      </article>
  </a>`;
};

/**
 * This function will take a Date in unix format and convert it in a string. The mont will be taken from an array with italian month names
 * @param {Date} unixDate
 * @returns {string} a string with the converted date
 */
const unixDateToString = (unixDate) => {
  return `${unixDate.getDate()} ${months[unixDate.getMonth()]} ${unixDate.getFullYear()}`;
};

const getParselyNodeObj = (parselyTopNewsId) => document.querySelector(`#${parselyTopNewsId}`);

const getParselyUrl = (parselyTopNewsId) => {
  const parselyNodeObject = getParselyNodeObj(parselyTopNewsId);
  if (parselyNodeObject) {
    return parselyNodeObject.dataset.url;
  }
};

const getElementsToRender = (parselyTopNewsId) => {
  const parselyNodeObject = getParselyNodeObj(parselyTopNewsId);
  if (parselyNodeObject && typeof parselyNodeObject.dataset.number !== 'undefined') {
    return parselyNodeObject.dataset.number;
  }
  return DEFAULT_ELEMENTS_TO_RENDER;
};

const getParselyType = (parselyTopNewsId) => {
  const parselyNodeObject = getParselyNodeObj(parselyTopNewsId);
  if (parselyNodeObject) {
    return parselyNodeObject.dataset.position;
  }
};

const getIntcmp = (parselyTopNewsId) =>
  document.querySelector(`#${parselyTopNewsId}`)
    ? document.querySelector(`#${parselyTopNewsId}`).getAttribute('data-parselyintcmp')
    : undefined;

/**
 * If the URL exist, the json will be fetched and the cards will be rendered in the page
 */
const initParselyRender = (parselyTopNewsId = 'parselyTopNews', nodeToHidrateId = 'parselySetOfCards') => {
  const parselyUrlTopNews = getParselyUrl(parselyTopNewsId);
  const parselyType = getParselyType(parselyTopNewsId);
  const uuid = window.PARSELY.config.parsely_site_uuid;
  const isRecommendationEnabled = isParselyRecommendationEnabled(parselyTopNewsId);

  const parselyUrl = isRecommendationEnabled === 'true' ? `${parselyUrlTopNews}&uuid=${uuid}` : parselyUrlTopNews;
  logger.info(`the parselyType is ${parselyType} --- parsely top news URL to fetch is --- ${parselyUrl}`);

  if (parselyUrl) {
    fetch(parselyUrl)
      .then((resp) => resp.json())
      .then((json) => {
        const $nodeToHydrate = document.querySelector(`#${nodeToHidrateId}`);

        if (parselyType === 'article') {
          let cards = ``;
          const elementsToRender = getElementsToRender(parselyTopNewsId);
          const parselyMeta = json.data.slice(0, elementsToRender);
          parselyMeta.forEach((articleMeta, index) => {
            const entertainmentChannel = articleMeta.tags
              .filter((elem) => elem === SKYUNO_LABEL || elem === SKYCINEMA_LABEL || elem === SKYATLANTIC_LABEL)
              .shift(); /* get first elem of array filtered by sky uno OR sky cinema OR sky atlantic */

            const cardClass =
              index === 0
                ? 'c-card c-card--base c-card--CA10-m c-card--CA15-t c-card--CA15-d'
                : 'c-card c-card--base c-card--CA05C-m c-card--CA15-t c-card--CA15-d';
            let pubdate = new Date(articleMeta.pub_date);
            pubdate = unixDateToString(pubdate);
            articleMeta.hasDate = true;
            articleMeta.fe_date = pubdate;
            articleMeta.channel = entertainmentChannel || '';
            if (articleMeta.channel) {
              articleMeta.cardLabelWrapper = true;
            }

            articleMeta.card_class = cardClass;
            const intcmp = getIntcmp(parselyTopNewsId);
            intcmp !== undefined ? (articleMeta.intcmp = intcmp) : null;

            const layoutPopulated = cardMarkup(articleMeta, parselyTopNewsId);
            cards = cards + layoutPopulated;
          });
          $nodeToHydrate.innerHTML = cards;
        } else if (parselyType === 'rail') {
          let cards = ``;
          const parselyMeta = json.data.slice(0, DEFAULT_ELEMENTS_TO_RENDER_RAIL);

          parselyMeta.forEach((articleMeta) => {
            const entertainmentChannel = articleMeta.tags
              .filter((elem) => elem === SKYUNO_LABEL || elem === SKYCINEMA_LABEL || elem === SKYATLANTIC_LABEL)
              .shift(); /* get first elem of array filtered by sky uno OR sky cinema OR sky atlantic */
            const cardClass = 'c-card c-card--base c-card--CA15-m c-card--CA05C-t c-card--CA05C-d'; // set of card R4B.
            articleMeta.hasDate = false;
            articleMeta.channel = entertainmentChannel || '';
            if (articleMeta.channel) {
              articleMeta.cardLabelWrapper = true;
            }

            articleMeta.card_class = cardClass;
            const intcmp = getIntcmp(parselyTopNewsId);
            intcmp !== undefined ? (articleMeta.intcmp = intcmp) : null;

            const layoutPopulated = cardMarkup(articleMeta, parselyTopNewsId);
            cards = cards + layoutPopulated;
          });

          $nodeToHydrate.innerHTML = cards;
        }
        /**
         * Initialize Datalayer Intcmp/Zoneid
         */
        initDatalayerElement($nodeToHydrate);
      })
      .catch((error) => {
        throw error;
      });
  } else {
    throw new Error('Invalid parsely Url in parsely render DOM element');
  }
};

export { initParselyRender };
